import axios from 'axios';
import { store } from '../store/store';
import { setAccessToken, LOGOUT_ACTION } from '../store/actions/AuthActions';
import { refreshBrowserSession, clearBrowserIdentity, sessionMarker, sessionChangedError } from './BrowserSessionService';

const axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API_BASE_URL, withCredentials: true });
function hideRequestDetails(value) {
    // Callers often log Axios errors/responses. Request metadata contains the
    // Authorization header, invitation tokens and other submitted credentials.
    if (value && typeof value === 'object') {
        delete value.config;
        delete value.request;
        if (value.response) { delete value.response.config; delete value.response.request; }
    }
    return value;
}
axiosInstance.interceptors.request.use(config => {
    config.headers = config.headers || {};
    if (config.skipAuth) {
        config.withCredentials = false;
        return config;
    }
    const accessToken = store.getState().auth.accessToken;
    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
    config._sessionMarker = sessionMarker();
    return config;
}, error => Promise.reject(error));

axiosInstance.interceptors.response.use(response => {
    if (response.config && !response.config.skipAuth && response.config._sessionMarker !== sessionMarker()) throw sessionChangedError();
    return hideRequestDetails(response);
}, async error => {
    const request = error.config;
    if (error.response?.status !== 401 || !request || request._retry || request.skipAuth ||
        request.skipSessionRefresh || /\/auth\/(refresh(?:-token)?|logout(?:-all)?)(?:[?]|$)/.test(request.url || '')) {
        throw hideRequestDetails(error);
    }
    if (request._sessionMarker !== sessionMarker()) throw sessionChangedError();
    request._retry = true;
    try {
        const accessToken = await refreshBrowserSession();
        store.dispatch(setAccessToken(accessToken));
        request.headers = { ...request.headers, Authorization: `Bearer ${accessToken}` };
        return axiosInstance(request);
    } catch (refreshError) {
        // Outages are retryable; they must not erase a valid session.
        if (refreshError.response?.status === 401) {
            store.dispatch({ type: LOGOUT_ACTION });
            await clearBrowserIdentity().catch(() => {});
        }
        throw hideRequestDetails(refreshError);
    }
});
export default axiosInstance;
